import React, { useState } from 'react';
import { auth, googleProvider } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, AlertCircle, Mail, Lock, UserPlus, LogIn, CheckCircle, Crown, Sparkles, ArrowRight, Search, Zap, TrendingUp, BarChart3 } from 'lucide-react';

const Login: React.FC = () => {
  const { error: contextError } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth) {
        setError("Dịch vụ xác thực chưa sẵn sàng. Vui lòng kiểm tra cấu hình.");
        return;
    }
    setLoading(true);
    setError('');

    try {
      if (showReset) {
        await auth.sendPasswordResetEmail(email);
        setResetSent(true);
        setTimeout(() => {
            setShowReset(false);
            setResetSent(false);
            setIsLogin(true);
        }, 5000);
      } else if (isLogin) {
        await auth.signInWithEmailAndPassword(email, password);
      } else {
        await auth.createUserWithEmailAndPassword(email, password);
      }
    } catch (err: any) {
      console.error("Auth Error:", err);
      const errorCode = err.code;
      
      // Handle specific Firebase Auth errors
      if (
          errorCode === 'auth/invalid-credential' || 
          errorCode === 'auth/user-not-found' || 
          errorCode === 'auth/wrong-password'
      ) {
          setError(isLogin ? 'Email hoặc mật khẩu không chính xác.' : 'Thông tin đăng ký không hợp lệ.');
      } else if (errorCode === 'auth/email-already-in-use') {
          setError('Email này đã được sử dụng. Vui lòng đăng nhập.');
      } else if (errorCode === 'auth/weak-password') {
          setError('Mật khẩu quá yếu. Hãy chọn mật khẩu mạnh hơn.');
      } else if (errorCode === 'auth/too-many-requests') {
          setError('Quá nhiều lần thử thất bại. Vui lòng đợi giây lát.');
      } else {
          // General error fallback
          setError(err.message || 'Đã xảy ra lỗi không xác định.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
      if (!auth || !googleProvider) {
          setError("Chưa cấu hình Google Login.");
          return;
      }
      setLoading(true);
      setError('');
      try {
          await auth.signInWithPopup(googleProvider);
      } catch (err: any) {
          console.error("Google Login Error:", err);
          if (err.code === 'auth/popup-closed-by-user') {
              setError("Bạn đã đóng cửa sổ đăng nhập.");
          } else if (err.code === 'auth/invalid-credential') {
              setError("Chứng thực không hợp lệ. Vui lòng thử lại.");
          } else {
              setError("Lỗi đăng nhập Google: " + (err.message || 'Unknown'));
          }
      } finally {
          setLoading(false);
      }
  };

  const FeatureItem = ({ icon: Icon, title, desc }: { icon: any, title: string, desc: string }) => (
      <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all group cursor-default">
          <div className="p-3 bg-stone-900/50 rounded-xl text-brand-500 shrink-0 group-hover:text-brand-400 group-hover:scale-110 transition-all border border-white/5 shadow-sm">
              <Icon size={24} />
          </div>
          <div>
              <h4 className="text-stone-200 font-bold text-sm mb-1">{title}</h4>
              <p className="text-stone-400 text-xs leading-relaxed">{desc}</p>
          </div>
      </div>
  );

  return (
    <div className="min-h-screen bg-stone-950 text-stone-200 font-sans selection:bg-brand-500/30 overflow-x-hidden flex flex-col relative">
      <h1 className="sr-only">SEO Mastermind AI</h1>

      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.8\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' opacity=\'1\'/%3E%3C/svg%3E")'}}></div>
          <div className="absolute -top-[20%] -right-[10%] w-[70vw] h-[70vw] rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.08)_0%,transparent_70%)] blur-[100px]"></div>
          <div className="absolute bottom-[10%] -left-[10%] w-[50vw] h-[50vw] rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.05)_0%,transparent_70%)] blur-[80px]"></div>
      </div>

      {/* NAVBAR */}
      <header className="relative z-20 w-full max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-brand-700 rounded-xl flex items-center justify-center shadow-[0_0_15px_rgba(245,158,11,0.3)] ring-1 ring-white/10">
                  <Crown className="text-white fill-white" size={20}/>
              </div>
              <div>
                  <div className="text-lg font-bold text-white leading-none tracking-tight">SEO Master</div>
                  <div className="text-[10px] font-bold text-brand-500 uppercase tracking-widest mt-0.5">Premium Suite</div>
              </div>
          </div>
      </header>

      {/* CONTENT */}
      <main className="relative z-10 flex-1 w-full max-w-7xl mx-auto px-6 py-4 lg:py-12 flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          
          {/* LEFT */}
          <div className="flex-1 space-y-8 lg:space-y-10 animate-slide-up w-full max-w-2xl">
              <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-950/50 border border-brand-500/20 text-brand-400 text-[11px] font-bold backdrop-blur-md shadow-sm">
                      <Sparkles size={12} className="fill-brand-400"/>
                      <span>Phiên bản 2.6: Gemini 3 Pro Vision</span>
                  </div>
                  
                  <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.15]">
                      Nội dung chuẩn SEO.<br/>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 via-brand-500 to-amber-200">Đột phá thứ hạng.</span>
                  </h2>
                  
                  <p className="text-base sm:text-lg text-stone-400 leading-relaxed max-w-lg">
                      Hệ thống tự động hóa quy trình SEO Content từ A-Z.
                  </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FeatureItem icon={Search} title="Nghiên cứu từ khóa" desc="Volume & Intent Analysis" />
                  <FeatureItem icon={Zap} title="Trợ lý viết bài E-E-A-T" desc="Content Automation" />
                  <FeatureItem icon={TrendingUp} title="Phân tích đối thủ" desc="Strategy & Gap Analysis" />
                  <FeatureItem icon={BarChart3} title="Kiểm toán SEO" desc="Scoring & User Simulation" />
              </div>
          </div>

          {/* RIGHT: FORM */}
          <div className="w-full max-w-[440px] shrink-0 animate-slide-in" style={{animationDelay: '0.1s'}}>
             <div className="bg-stone-900/80 backdrop-blur-xl rounded-[2rem] shadow-2xl shadow-black/50 border border-white/10 overflow-hidden relative group">
                 <div className="h-1.5 w-full bg-gradient-to-r from-transparent via-brand-500 to-transparent opacity-60"></div>
                 
                 <div className="p-8 sm:p-10">
                    <div className="text-center mb-6">
                       <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">
                           {showReset ? "Khôi phục mật khẩu" : (isLogin ? "Chào mừng trở lại" : "Tạo tài khoản mới")}
                       </h3>
                       <p className="text-stone-500 text-sm">
                           {showReset ? 'Email' : (isLogin ? "Đăng nhập để tiếp tục hành trình lên Top" : "Dùng thử miễn phí, không cần thẻ tín dụng")}
                       </p>
                    </div>

                    {(error || contextError) && (
                       <div className="bg-red-500/10 text-red-400 p-4 rounded-xl text-xs font-bold flex items-start gap-3 mb-6 border border-red-500/20 animate-shake">
                          <AlertCircle size={16} className="mt-0.5 shrink-0"/> 
                          <span>{error || contextError}</span>
                       </div>
                    )}
                    
                    {resetSent && (
                       <div className="bg-green-500/10 text-green-400 p-4 rounded-xl text-xs font-bold flex items-start gap-3 mb-6 border border-green-500/20">
                          <CheckCircle size={16} className="mt-0.5 shrink-0"/> 
                          <span>Sent!</span>
                       </div>
                    )}

                    {!showReset && (
                        <>
                            <button 
                                onClick={handleGoogleLogin}
                                disabled={loading || !auth}
                                className="w-full bg-white text-stone-900 font-bold py-3.5 rounded-xl hover:bg-stone-100 transition-all flex items-center justify-center gap-3 shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed mb-5 relative overflow-hidden group"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                                </svg>
                                Tiếp tục với Google
                            </button>

                            <div className="relative flex py-2 items-center mb-5">
                                <div className="flex-grow border-t border-white/10"></div>
                                <span className="flex-shrink-0 mx-4 text-stone-500 text-[10px] uppercase font-bold tracking-widest">Hoặc</span>
                                <div className="flex-grow border-t border-white/10"></div>
                            </div>
                        </>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                       <div className="space-y-1.5">
                          <label className="text-[11px] font-bold text-stone-500 uppercase ml-1">Email</label>
                          <div className="relative group">
                              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 group-focus-within:text-brand-500 transition-colors" size={18}/>
                              <input 
                                type="email" 
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full pl-12 pr-5 py-3.5 rounded-xl border border-white/10 bg-black/40 text-stone-200 placeholder:text-stone-600 focus:bg-black/60 focus:ring-1 focus:ring-brand-500 focus:border-brand-500/50 outline-none transition-all font-medium text-sm"
                                placeholder="name@example.com"
                              />
                          </div>
                       </div>
                       
                       {!showReset && (
                           <div className="space-y-1.5">
                              <label className="text-[11px] font-bold text-stone-500 uppercase ml-1">Mật khẩu</label>
                              <div className="relative group">
                                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 group-focus-within:text-brand-500 transition-colors" size={18}/>
                                  <input 
                                    type="password" 
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-12 pr-5 py-3.5 rounded-xl border border-white/10 bg-black/40 text-stone-200 placeholder:text-stone-600 focus:bg-black/60 focus:ring-1 focus:ring-brand-500 focus:border-brand-500/50 outline-none transition-all font-medium text-sm"
                                    placeholder="••••••••"
                                    minLength={6}
                                  />
                              </div>
                           </div>
                       )}
                       
                       {isLogin && !showReset && (
                           <div className="flex justify-between items-center text-xs pt-1">
                               <label className="flex items-center gap-2 cursor-pointer text-stone-500 hover:text-stone-300 transition-colors">
                                   <div className="relative flex items-center">
                                       <input type="checkbox" className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-stone-600 bg-stone-900 transition-all checked:border-brand-500 checked:bg-brand-500"/>
                                       <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100">
                                           <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                       </div>
                                   </div>
                                   Ghi nhớ đăng nhập
                               </label>
                               <button type="button" onClick={() => {setShowReset(true); setError('');}} className="font-bold text-stone-400 hover:text-brand-400 transition-colors">
                                   Quên mật khẩu?
                               </button>
                           </div>
                       )}
                       
                       <button 
                          type="submit" 
                          disabled={loading || !auth}
                          className="w-full py-4 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-500 hover:to-brand-600 text-white rounded-xl font-bold transition-all shadow-lg shadow-brand-900/20 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed group relative overflow-hidden"
                       >
                          <span className="relative flex items-center gap-2">
                              {loading ? <Loader2 className="animate-spin"/> : (
                                  showReset ? "Gửi liên kết" : 
                                  (isLogin ? <><LogIn size={18}/> Đăng nhập bằng Email</> : <><UserPlus size={18}/> Đăng ký bằng Email</>)
                              )}
                          </span>
                       </button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-white/5 text-center">
                        {!showReset ? (
                           <button 
                              onClick={() => { setIsLogin(!isLogin); setError(''); }}
                              className="text-sm font-medium text-stone-500 hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto group"
                           >
                              {isLogin ? (
                                  <>Chưa có tài khoản? <span className="text-brand-500 font-bold group-hover:underline">Đăng ký ngay</span> <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform"/></>
                              ) : (
                                  <>Đã có tài khoản? <span className="text-brand-500 font-bold group-hover:underline">Đăng nhập</span></>
                              )}
                           </button>
                        ) : (
                            <button 
                              onClick={() => { setShowReset(false); setError(''); }}
                              className="text-sm font-bold text-stone-500 hover:text-white transition-colors flex items-center gap-2 justify-center"
                            >
                               Quay lại Đăng nhập
                            </button>
                        )}
                    </div>
                 </div>
             </div>
          </div>
      </main>
    </div>
  );
};

export default Login;